import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Search,
  Loader2,
  HelpCircle,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Send,
  MessageCircle,
  CheckCircle2,
  Clock,
  Sparkles,
  ChevronRight
} from "lucide-react";
import * as LucideIcons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["all", "Price", "Timing", "Competition", "Trust", "Need", "Authority"];

const Objections = () => {
  const { user } = useAuth();
  const [objections, setObjections] = useState([]);
  const [myQuestions, setMyQuestions] = useState([]);
  const [categories, setCategories] = useState(CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedObjection, setSelectedObjection] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [askDialogOpen, setAskDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newResponse, setNewResponse] = useState("");
  const [askFormData, setAskFormData] = useState({
    objection_text: "",
    category: "General",
    context: ""
  });

  useEffect(() => {
    fetchData();
  }, [categoryFilter]);

  const fetchData = async () => {
    try {
      // Fetch objections (public endpoint)
      const objectionsRes = await axios.get(`${API}/objections${categoryFilter !== "all" ? `?category=${categoryFilter}` : ""}`);
      setObjections(objectionsRes.data);
    } catch (error) {
      console.error("Error fetching objections:", error);
      toast.error("Failed to load objections");
    }
    
    try {
      // Fetch user's questions (requires auth)
      const questionsRes = await axios.get(`${API}/objections/my-questions`);
      setMyQuestions(questionsRes.data);
    } catch (error) {
      console.error("Error fetching my questions:", error);
      // Don't show error toast for this as it's secondary
    }
    
    setLoading(false);
  };

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Price": "bg-green-900/50 text-green-400 border-green-800",
      "Timing": "bg-blue-900/50 text-blue-400 border-blue-800",
      "Competition": "bg-purple-900/50 text-purple-400 border-purple-800",
      "Trust": "bg-amber-900/50 text-amber-400 border-amber-800",
      "Need": "bg-cyan-900/50 text-cyan-400 border-cyan-800",
      "Authority": "bg-pink-900/50 text-pink-400 border-pink-800"
    };
    return colors[category] || "bg-slate-700 text-slate-300 border-slate-600";
  };

  const handleMarkEffective = async (objectionId, responseIndex, worked) => {
    try {
      await axios.post(`${API}/objections/${objectionId}/mark-effective`, {
        response_index: responseIndex,
        worked
      });
      toast.success(worked ? "Marked as effective! 👍" : "Thanks for the feedback");
      // Refresh objection data
      const res = await axios.get(`${API}/objections/${objectionId}`);
      setSelectedObjection(res.data);
    } catch (error) {
      toast.error("Failed to record feedback");
    }
  };

  const handleAddResponse = async () => {
    if (!newResponse.trim()) return;
    
    try {
      await axios.post(`${API}/objections/${selectedObjection.id}/add-response`, {
        response_text: newResponse
      });
      toast.success("Your response has been added!");
      setNewResponse("");
      // Refresh
      const res = await axios.get(`${API}/objections/${selectedObjection.id}`);
      setSelectedObjection(res.data);
    } catch (error) {
      toast.error("Failed to add response");
    }
  };

  const handleUpvote = async (responseId) => {
    try {
      await axios.post(`${API}/objections/${selectedObjection.id}/upvote/${responseId}`);
      toast.success("Upvoted!");
      const res = await axios.get(`${API}/objections/${selectedObjection.id}`);
      setSelectedObjection(res.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You've already upvoted this response");
      } else {
        toast.error("Failed to upvote");
      }
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!askFormData.objection_text.trim()) {
      toast.error("Please enter your objection");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/objections/ask`, askFormData);
      toast.success("Question submitted! AI is generating a suggestion...");
      setAskDialogOpen(false);
      setAskFormData({ objection_text: "", category: "General", context: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  const getEffectivenessPercent = (stats, index) => {
    const key = `response_${index}`;
    const data = stats?.[key];
    if (!data) return 0;
    const total = (data.worked || 0) + (data.not_worked || 0);
    if (total === 0) return 0;
    return Math.round((data.worked / total) * 100);
  };

  const filteredObjections = objections.filter(obj =>
    obj.title.toLowerCase().includes(search.toLowerCase()) ||
    obj.description.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-2">
            <HelpCircle className="w-8 h-8 text-gba-green" />
            Objection Handling
          </h1>
          <p className="text-slate-400 mt-1">
            Master responses to common sales objections
          </p>
        </div>
        <Button
          onClick={() => setAskDialogOpen(true)}
          className="bg-gba-green hover:bg-gba-green-hover text-white font-bold shadow-glow"
          data-testid="ask-question-button"
        >
          <Plus className="w-5 h-5 mr-2" />
          Ask a Question
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="library" className="w-full">
        <TabsList className="bg-[#1E293B] border border-slate-700 p-1">
          <TabsTrigger
            value="library"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="library-tab"
          >
            Objection Library ({objections.length})
          </TabsTrigger>
          <TabsTrigger
            value="my-questions"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="my-questions-tab"
          >
            My Questions ({myQuestions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-4 space-y-4">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search objections..."
                className="pl-10 bg-[#1E293B] border-slate-700 text-white"
                data-testid="objection-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-[#1E293B] border-slate-700 text-white">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-slate-700">
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat === "all" ? "All Categories" : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Objections Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredObjections.map((objection, index) => (
              <Card
                key={objection.id}
                className="bg-[#1E293B] border-slate-700 card-hover cursor-pointer animate-fade-in"
                onClick={() => {
                  setSelectedObjection(objection);
                  setDetailDialogOpen(true);
                }}
                data-testid={`objection-card-${objection.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center flex-shrink-0 text-red-400">
                      {getIcon(objection.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white mb-1 line-clamp-1">
                        "{objection.title}"
                      </h3>
                      <p className="text-sm text-slate-400 line-clamp-2 mb-2">
                        {objection.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getCategoryColor(objection.category)}`}>
                          {objection.category}
                        </span>
                        <span className="text-xs text-slate-500">
                          {objection.suggested_responses?.length || 0} responses
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredObjections.length === 0 && (
            <Card className="bg-[#1E293B] border-slate-700 border-dashed">
              <CardContent className="p-12 text-center">
                <Search className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white mb-2">No objections found</h3>
                <p className="text-slate-400">Try adjusting your search or filter</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="my-questions" className="mt-4">
          {myQuestions.length === 0 ? (
            <Card className="bg-[#1E293B] border-slate-700 border-dashed">
              <CardContent className="p-12 text-center">
                <MessageCircle className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white mb-2">No questions yet</h3>
                <p className="text-slate-400 mb-4">Ask about objections you encounter in the field</p>
                <Button
                  onClick={() => setAskDialogOpen(true)}
                  className="bg-gba-green hover:bg-gba-green-hover"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ask Your First Question
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {myQuestions.map((question) => (
                <Card key={question.id} className="bg-[#1E293B] border-slate-700">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            question.status === "answered" 
                              ? "bg-gba-green/20 text-gba-green"
                              : question.status === "ai_suggested"
                              ? "bg-purple-900/30 text-purple-400"
                              : "bg-amber-900/30 text-amber-400"
                          }`}>
                            {question.status === "answered" ? "Answered" : 
                             question.status === "ai_suggested" ? "AI Suggested" : "Pending"}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(question.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="font-medium text-white mb-2">
                          "{question.objection_text}"
                        </h3>
                        {question.context && (
                          <p className="text-sm text-slate-500 mb-3">
                            Context: {question.context}
                          </p>
                        )}
                        
                        {question.ai_suggestion && (
                          <div className="bg-purple-900/20 border border-purple-800/50 rounded-lg p-3 mb-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-4 h-4 text-purple-400" />
                              <span className="text-xs font-medium text-purple-400">AI Suggestion</span>
                            </div>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap">
                              {question.ai_suggestion}
                            </p>
                          </div>
                        )}
                        
                        {question.admin_reply && (
                          <div className="bg-gba-green/10 border border-gba-green/30 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle2 className="w-4 h-4 text-gba-green" />
                              <span className="text-xs font-medium text-gba-green">Coach Reply</span>
                            </div>
                            <p className="text-sm text-slate-300 whitespace-pre-wrap">
                              {question.admin_reply}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Objection Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedObjection && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-900/30 flex items-center justify-center text-red-400 flex-shrink-0">
                    {getIcon(selectedObjection.icon)}
                  </div>
                  <div>
                    <span className="text-white text-xl">"{selectedObjection.title}"</span>
                    <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium border ${getCategoryColor(selectedObjection.category)}`}>
                      {selectedObjection.category}
                    </span>
                    <p className="text-slate-400 text-sm font-normal mt-1">
                      {selectedObjection.description}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                <h4 className="font-medium text-white">Suggested Responses</h4>
                
                <ScrollArea className="max-h-[300px]">
                  <div className="space-y-3">
                    {selectedObjection.suggested_responses?.map((response, index) => {
                      const effectiveness = getEffectivenessPercent(selectedObjection.effectiveness_stats, index);
                      return (
                        <div
                          key={response.id || index}
                          className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
                        >
                          <p className="text-slate-300 text-sm mb-3">
                            "{response.response}"
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {response.source === "gba" ? (
                                <span className="text-xs text-gba-green">GBA Recommended</span>
                              ) : (
                                <span className="text-xs text-slate-500">
                                  By {response.user_name || "Community"}
                                </span>
                              )}
                              {effectiveness > 0 && (
                                <div className="flex items-center gap-1">
                                  <Progress value={effectiveness} className="w-16 h-1.5 bg-slate-700" />
                                  <span className="text-xs text-gba-green">{effectiveness}%</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {response.upvotes > 0 && (
                                <span className="text-xs text-slate-500 flex items-center gap-1">
                                  <ThumbsUp className="w-3 h-3" /> {response.upvotes}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkEffective(selectedObjection.id, index, true);
                                }}
                                className="text-gba-green hover:text-gba-green hover:bg-gba-green/10"
                                data-testid={`mark-worked-${index}`}
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkEffective(selectedObjection.id, index, false);
                                }}
                                className="text-slate-400 hover:text-red-400 hover:bg-red-900/20"
                                data-testid={`mark-not-worked-${index}`}
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </Button>
                              {response.id && response.source !== "gba" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpvote(response.id);
                                  }}
                                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
                                >
                                  <ThumbsUp className="w-4 h-4 mr-1" /> Upvote
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>

                {/* Add Your Response */}
                <div className="border-t border-slate-700 pt-4">
                  <h4 className="font-medium text-white mb-3">Add Your Response</h4>
                  <p className="text-sm text-slate-400 mb-3">
                    Share a response that worked for you to help the community
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={newResponse}
                      onChange={(e) => setNewResponse(e.target.value)}
                      placeholder="What did you say that worked?"
                      className="flex-1 bg-[#0F172A] border-slate-700 text-white"
                      data-testid="add-response-input"
                    />
                    <Button
                      onClick={handleAddResponse}
                      disabled={!newResponse.trim()}
                      className="bg-gba-green hover:bg-gba-green-hover"
                      data-testid="submit-response-button"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Ask Question Dialog */}
      <Dialog open={askDialogOpen} onOpenChange={setAskDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-gba-green" />
              Ask About an Objection
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAskQuestion} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">What objection did you hear? *</Label>
              <textarea
                value={askFormData.objection_text}
                onChange={(e) => setAskFormData({ ...askFormData, objection_text: e.target.value })}
                placeholder='e.g., "We already have a solution in place..."'
                className="w-full h-24 px-3 py-2 bg-[#0F172A] border border-slate-700 rounded-lg text-white placeholder:text-slate-600 resize-none"
                data-testid="ask-objection-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select
                value={askFormData.category}
                onValueChange={(value) => setAskFormData({ ...askFormData, category: value })}
              >
                <SelectTrigger className="bg-[#0F172A] border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-slate-700">
                  {CATEGORIES.filter(c => c !== "all").map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Context (optional)</Label>
              <Input
                value={askFormData.context}
                onChange={(e) => setAskFormData({ ...askFormData, context: e.target.value })}
                placeholder="e.g., B2B software sale, first meeting..."
                className="bg-[#0F172A] border-slate-700 text-white"
                data-testid="ask-context-input"
              />
            </div>

            <div className="bg-purple-900/20 border border-purple-800/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-400">AI-Powered</span>
              </div>
              <p className="text-xs text-slate-400">
                Our AI will generate suggested responses, and a coach will review and provide additional guidance.
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 uppercase tracking-wide"
              data-testid="submit-question-button"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Question
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Objections;
